import { useEffect, useRef, useState } from 'react';
import { WALL_TYPE } from '@cx-sdk/cvms';
import classNames from 'classnames';
import { Menu } from 'lucide-react';
import { ClientOnly } from 'remix-utils/client-only';

import { REPUBS } from '@cx-modules/constants';
import { type Country, type Repub } from '@cx-modules/types';

import Authentication from '~/components/Authentication';
import {
  Dialog as AuthDialog,
  DialogContent,
  DialogTitle } from
'~/components/Authentication/components/Dialog';
import Betslip from '~/components/betslip/betslip';
import MiniBetslip from '~/components/betslip/components/mini-betslip';
import miniBetslipStyles from '~/components/betslip/style/mini-betslip.module.css';
import Icon from '~/components/icon';
import { LegacyAuthModal } from '~/components/legacy-auth-modal';
import { positiveEvIndex } from '~/configuration/betting-insights/positive-ev';
import type ODDS_FORMAT from '~/enums/odds_format';
import {
  useAuthModal,
  VIEW_VERIFY_PAYMENT } from
'~/providers/auth-modal-provider';
import { useBettingInsightsConfig } from '~/providers/betting-insights-config-provider';
import { useLoaderContext } from '~/providers/loader-context';
import { useServicesContext } from '~/providers/services-context.tsx';
import { MyOC } from '~/scripts/myoddschecker/MyOC';
import { type UserSession } from '~/services/session.server';
import { type Subscription } from '~/types/myoc';
import { pollForWindowProperty } from '~/utils/pollForWindowObject';
import { comp } from '~/utils/us-utils';

import { Button, Dialog } from '../cxy';

import styles from './styles/Header.module.css';
import { HeaderItems } from './HeaderItems';
import { MenuList } from './MenuList';

export interface HeaderProps {
  BETSLIP_SCRIPT_URL: string;
  apiKey: string;
  baseUrl: string;
  countryCode: Country;
  currentUrl: string;
  isTipstersEnabled: boolean;
  oddsFormat: ODDS_FORMAT;
  repub: Repub;
  resourcesUrl: string;
  subscriptions?: Subscription[];
  user?: UserSession;
}

export const Header = ({
  BETSLIP_SCRIPT_URL,
  apiKey,
  baseUrl,
  countryCode,
  currentUrl,
  isTipstersEnabled,
  oddsFormat,
  repub,
  resourcesUrl,
  subscriptions,
  user
}: HeaderProps) => {
  const { currentPathWithRepub } = useLoaderContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const { isOpen, toggle, view } = useAuthModal();
  const config = useBettingInsightsConfig(positiveEvIndex);
  const isPaywalled = config.isWalled && config.wallType === WALL_TYPE.PAYWALL;
  const isVerifyingPayment = view === VIEW_VERIFY_PAYMENT;
  const { labelActions } = useServicesContext();

  const [betCount, setBetCount] = useState(0);

  useEffect(() => {
    if (repub !== REPUBS.OC.name) return;
    window.oc = {
      betslipAdvertActive: false
    };

    let xpId = '';
    if (typeof window.xtremepush !== 'undefined') {
      const xpDeviceInfo = window.xtremepush('get', 'device_info');
      if (typeof xpDeviceInfo !== 'undefined') {
        xpId = xpDeviceInfo.id;
      }
    }

    const loadBetslip = async () => {
      const script = document.createElement('script');
      script.src = BETSLIP_SCRIPT_URL;
      script.type = 'text/javascript';
      script.async = true;
      document.head.appendChild(script);

      try {
        await pollForWindowProperty('ocRenderers.integrated-betslip');
      } catch {
        console.warn('OC Betslip renderer not present');
        return;
      }

      const initBetslip = window.ocRenderers?.['integrated-betslip'];

      initBetslip({
        apiKey,
        deviceId: user?.deviceId,
        oddsFormat: oddsFormat ?? 'fractional',
        repub,
        xpId
      });

      try {
        await pollForWindowProperty('ocBetslip');
        const betslip = window.ocBetslip;
        betslip.setCallbacks({
          setBetCount: () => setBetCount(betslip.getBetCount())
        });
        setBetCount(window.ocBetslip.getBetCount());
      } catch {
        console.warn('OC Betslip not loaded');
      }
    };

    loadBetslip();
  }, [BETSLIP_SCRIPT_URL, apiKey, oddsFormat, repub, user?.deviceId]);

  return (
    <>
			<header className={styles.header} data-testid="oc-plus-header">
				<a
          className={styles.isVisiblyHidden}
          href={`${currentPathWithRepub}#main`}>

					Skip to main content
				</a>

				<Dialog
          className={styles.scroll}
          isFullScreen
          isOpen={isMenuOpen}
          setIsOpen={setIsMenuOpen}
          title="navigation"
          triggerRef={ref}>

					<div style={{ width: '100%' }}>
						<h3>Menu</h3>
						<HeaderItems
              baseUrl={baseUrl}
              currentPathWithRepub={currentPathWithRepub || baseUrl}
              isTipstersEnabled={isTipstersEnabled}
              repub={repub}
              resourcesUrl={resourcesUrl} />

					</div>
				</Dialog>

				<nav aria-label="Main" className={styles.navigation}>
					<Dialog.Trigger
            aria-label="Toggle navigation Menu"
            // @ts-ignore
            className={styles.menuButton}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            ref={ref}>

						<Menu />
					</Dialog.Trigger>

					<HeaderItems
            baseUrl={baseUrl}
            currentPathWithRepub={currentPathWithRepub || baseUrl}
            isTipstersEnabled={isTipstersEnabled}
            repub={repub}
            resourcesUrl={resourcesUrl} />


					<div className={styles.userButtons}>
						{repub !== REPUBS.OC.name &&
            <MenuList isLoggedIn={!!user} subscriptions={subscriptions} />
            }
						<Button
              aria-label="toggle betslip"
              className={styles.betslipButton}
              variant="outline"
              {...repub === REPUBS.OC.name && {
                onClick: () => {
                  window.ocBetslip.toggle();
                }
              }}>

							{repub === REPUBS.OC.name ?
              <div
                className={miniBetslipStyles.wrapper}
                data-testid="betslip-button">

									<div
                  className={classNames(
                    miniBetslipStyles.miniBetslip,
                    styles.betslipMini
                  )}>

										<Icon height={20} icon="Betslip" width={20} />
										{betCount > 0 &&
                  <span
                    className={comp(
                      miniBetslipStyles.betCount,
                      betCount > 0 ? miniBetslipStyles.betAdded : ''
                    )}>

												{betCount}
											</span>
                  }
									</div>
									<span className={miniBetslipStyles.betslipText}>Betslip</span>
								</div> :

              <ClientOnly fallback={null}>
									{() =>
                <MiniBetslip
                  betslipTitle={labelActions.get('RESPONSIVE_BETSLIP')}
                  className={styles.betslipMini}
                  countryCode={countryCode} />

                }
								</ClientOnly>
              }
						</Button>
					</div>
				</nav>

				<ClientOnly fallback={null}>
					{() =>
          <>
							{repub !== REPUBS.OC.name &&
            <Betslip
              betslipOpen={true}
              className={styles.betslip}
              country={countryCode} />

            }
							<MyOC />
							<LegacyAuthModal />
						</>
          }
				</ClientOnly>
			</header>

			<AuthDialog onOpenChange={toggle} open={isOpen}>
				<DialogContent aria-describedby={undefined}>
					<DialogTitle style={{ display: 'none' }}>
						Log in or Sign up
					</DialogTitle>
					<Authentication>
						<Authentication.InfoSection resourcesUrl={resourcesUrl} view={view}>
							{isPaywalled && !isVerifyingPayment &&
              <Authentication.InfoSection.InfoLink repub={repub} />
              }
						</Authentication.InfoSection>
						<Authentication.AuthSection
              currentUrl={currentUrl}
              repub={repub}
              view={view} />

					</Authentication>
				</DialogContent>
			</AuthDialog>
		</>);

};